import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "next-i18next";
import { useDispatch } from "react-redux";
import { Skeleton } from "@mui/material";
import { useTheme } from "@mui/material";
import { useTradeInfo } from "@/redux/selectors/tradeInfoSelector";
// import AssetsTable from "./AssetsTable";
import { setActiveGroupName } from "@/redux/actions/tradeInfo";
import { useUI } from "@/redux/selectors/uiSelector";
import styles from "./TradingAssetsSection.module.scss";

export const favouritesCategory = {
    name: "Favourites",
    description: "",
};

const SwiperItem = ({ handleChange, label, isActive, name, isAssetsTableExpanded }) => {
    return (
        <div
            className={clsx(styles.categoryListItem, {
                [styles.categoryListItemActive]: isActive,
                [styles.listItemExpanded]: isAssetsTableExpanded,
            })}
            onClick={() => handleChange(name)}
            data-value={name}>
            {label}
        </div>
    );
};

export const CategoriesSwiper = ({ isSearching }) => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    const { palette } = useTheme();

    const tabsContainerRef = useRef(null);

    const {
        isQuoteDataLoaded,
        groups: assetCategories,
        activeGroupName: activeCategory,
        //search,
    } = useTradeInfo();
    const { isAssetsTableExpanded } = useUI();

    const categories = [favouritesCategory, ...assetCategories].filter(Boolean);

    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollButtons = () => {
        const container = tabsContainerRef.current;
        if (!container) return;

        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth);
    };

    const handleChange = name => {
        dispatch(setActiveGroupName(name));

        setTimeout(() => {
            const container = tabsContainerRef.current;
            const tab = container?.querySelector(`[data-value="${name}"]`);
            if (!container || !tab) return;

            const containerRect = container.getBoundingClientRect();
            const tabRect = tab.getBoundingClientRect();

            const offset =
                tabRect.left - containerRect.left - containerRect.width / 2 + tabRect.width / 2;

            container.scrollTo({
                left: container.scrollLeft + offset,
                behavior: "smooth",
            });
        }, 100);
    };

    const handleSlide = direction => {
        const container = tabsContainerRef.current;
        if (!container) return;

        const scrollAmount = 200;
        container.scrollBy({
            left: direction === "left" ? -scrollAmount : scrollAmount,
            behavior: "smooth",
        });
    };

    useEffect(() => {
        if (isQuoteDataLoaded && !activeCategory && assetCategories.length) {
            dispatch(setActiveGroupName(assetCategories[0].name));
        }
    }, [isQuoteDataLoaded, assetCategories, activeCategory, dispatch]);

    useEffect(() => {
        const container = tabsContainerRef.current;
        if (!container) return;

        updateScrollButtons();

        container.addEventListener("scroll", updateScrollButtons);
        window.addEventListener("resize", updateScrollButtons);

        return () => {
            container.removeEventListener("scroll", updateScrollButtons);
            window.removeEventListener("resize", updateScrollButtons);
        };
    }, []);

    useEffect(() => {
        const container = tabsContainerRef.current;
        if (!container) return;

        updateScrollButtons();
    }, [isAssetsTableExpanded]);

    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollStart, setScrollStart] = useState(0);

    const handleTouchStart = e => {
        const container = tabsContainerRef.current;
        if (!container) return;

        setIsDragging(true);
        setStartX(e.touches[0].clientX);
        setScrollStart(container.scrollLeft);
    };

    const handleTouchMove = e => {
        if (!isDragging) return;

        const container = tabsContainerRef.current;
        if (!container) return;

        const dx = e.touches[0].clientX - startX;
        container.scrollLeft = scrollStart - dx;
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    return (
        <div className={clsx(styles.swiper, { [styles.isExpanded]: isAssetsTableExpanded })}>
            {isSearching && <div className={styles.searchResultPlaceholder}>{t("search_results")}:</div>}

            {!isSearching && assetCategories.length ? (
                <>
                    {canScrollLeft && (
                        <div
                            onClick={() => handleSlide("left")}
                            className={clsx(
                                styles.navButton,
                                styles[`leftBtn_${palette?.mode}`],
                                styles.rippleButton
                            )}
                        />
                    )}

                    <div
                        ref={tabsContainerRef}
                        className={styles.categoriesContainer}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}>
                        {categories.map(({ name = "" }) => {
                            const isActive = activeCategory === name;
                            const nameLowerCase = name === "BITA" ? name : name.toLocaleLowerCase();
                            let translated =
                                nameLowerCase === "cryptocurrencies" ? t("crypto") : t(nameLowerCase);
                            if (translated === "bita") translated = name;

                            return (
                                <SwiperItem
                                    key={name}
                                    handleChange={handleChange}
                                    label={translated}
                                    //value={name}
                                    isActive={isActive}
                                    name={name}
                                    isAssetsTableExpanded={isAssetsTableExpanded}
                                />
                            );
                        })}
                    </div>

                    {canScrollRight && (
                        <div
                            onClick={() => handleSlide("right")}
                            className={clsx(
                                styles.navButton,
                                styles[`rightBtn_${palette?.mode}`],
                                styles.rippleButton
                            )}
                        />
                    )}
                </>
            ) : (
                <div style={{ width: "100%", padding: "0 0.5rem" }}>
                    {!isSearching && <Skeleton height="3.8rem" animation="wave" />}
                </div>
            )}
        </div>
    );
};
